import React, { useState } from "react";

import { elide } from "utils/string";
import classNames from "classnames";
import {
  faArrowsMaximize,
  faArrowsMinimize,
} from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Button from "components/Button";
import Tip from "components/Tip";

function QueryParameters({
  parameters,
}: {
  parameters: (string | null)[] | null;
}) {
  const [expanded, setExpanded] = useState(false);
  if (!parameters || parameters.length == 0) {
    return null;
  }

  // Only show up to 50 parameters by default
  const threshold = 50;
  const displayedParams = expanded
    ? parameters
    : parameters.slice(0, threshold);
  const expandable =
    parameters.length > threshold ||
    parameters.some((param) => param?.length > 20);

  const classes = classNames(
    "whitespace-pre-wrap border-none m-0 p-0 bg-transparent",
    expanded
      ? ""
      : CSS.supports("display: -webkit-box")
      ? "line-clamp-3"
      : "overflow-hidden max-h-14",
  );

  return (
    <>
      <pre className={classes}>
        {displayedParams.map((p, i) => {
          return (
            <React.Fragment key={i}>
              <Param n={i + 1} value={p} compact={!expanded} />
              {i < displayedParams.length - 1 && ", "}
            </React.Fragment>
          );
        })}
      </pre>
      {expandable && (
        <div className="pt-2">
          <Button
            variant="link"
            bare
            onClick={() => setExpanded((expanded) => !expanded)}
          >
            <FontAwesomeIcon
              icon={expanded ? faArrowsMinimize : faArrowsMaximize}
            />{" "}
            {expanded ? "Show truncated" : "Show full"} values
          </Button>
          <Tip
            className="ml-1"
            content={
              <>
                Sample values are extracted from the slow queries log. Values
                might be truncated even with "Show full values" depending on the
                log_parameter_max_length setting.
              </>
            }
          />
        </div>
      )}
    </>
  );
}

function Param({
  value,
  n,
  compact,
}: {
  value: string | null;
  n: number;
  compact: boolean;
}) {
  const title = paramStr(value);
  return (
    <span title={title}>
      ${n} = {paramStr(value, compact ? 20 : Infinity)}
    </span>
  );
}

function paramStr(param: string | null, maxLen: number = Infinity) {
  return param === null ? "NULL" : `'${elide(param, maxLen)}'`;
}

export default QueryParameters;
